import React, { useEffect, useMemo, useState } from 'react';
import { FiCheck, FiRefreshCw, FiX } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { catalogHashService, CatalogRefreshProgress, CatalogRefreshState } from '../../services/catalogHashService';
import { markCatalogBackendForceRefresh } from '../../utils/catalogRefresh';
import './CatalogUpdate.css';

interface CatalogUpdateProps {
  onClose: () => void;
}

export const CatalogUpdate: React.FC<CatalogUpdateProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const [progress, setProgress] = useState<CatalogRefreshProgress>({ live: 'waiting', vod: 'waiting', series: 'waiting' });
  const [runId, setRunId] = useState(0);
  const [retryKinds, setRetryKinds] = useState<Array<'live' | 'vod' | 'series'> | null>(null);
  const running = progress.live === 'running' || progress.vod === 'running' || progress.series === 'running';
  const finished = !running && [progress.live, progress.vod, progress.series].every((state) => state === 'done' || state === 'failed');

  useEffect(() => {
    let active = true;
    void catalogHashService.refreshSequential(true, (next) => { if (active) setProgress(next); }, retryKinds || undefined)
      .then(() => markCatalogBackendForceRefresh());
    return () => { active = false; };
  }, [retryKinds, runId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const code = event.keyCode || event.which || 0;
      const back = event.key === 'Escape' || event.key === 'GoBack' || event.key === 'BrowserBack' || event.keyCode === 10009 || event.keyCode === 461;
      const confirm = event.key === 'Enter' || event.key === 'OK' || event.key === 'Return' || event.key === ' ' || code === 13;
      if (back || (finished && confirm)) {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [finished, onClose]);

  const rows = useMemo(() => [
    { kind: 'live', label: t('catalogUpdate.live', 'Canais ao vivo'), state: progress.live },
    { kind: 'vod', label: t('catalogUpdate.movies', 'Filmes'), state: progress.vod },
    { kind: 'series', label: t('catalogUpdate.series', 'Séries'), state: progress.series },
  ], [progress, t]);

  const icon = (state: CatalogRefreshState) => {
    if (state === 'done') return <FiCheck />;
    if (state === 'failed') return <FiX />;
    return <FiRefreshCw className={state === 'running' ? 'catalog-update__spin' : ''} />;
  };

  return (
    <section className="catalog-update">
      <div className="catalog-update__panel">
        <h1>{t('catalogUpdate.title', 'Atualizar conteúdo')}</h1>
        <p>{t('catalogUpdate.subtitle', 'O conteúdo é atualizado por etapas. Uma falha não interrompe as outras etapas.')}</p>
        <div className="catalog-update__rows">
          {rows.map((row) => (
            <div className={`catalog-update__row catalog-update__row--${row.state}`} key={row.kind}>
              <span className="catalog-update__icon">{icon(row.state)}</span>
              <span>{row.label}</span>
              <strong>{t(`catalogUpdate.state.${row.state}`, row.state === 'waiting' ? 'Aguardando' : row.state === 'running' ? 'Atualizando' : row.state === 'done' ? 'Concluído' : 'Falhou')}</strong>
            </div>
          ))}
        </div>
        <div className="catalog-update__actions">
          {finished && progress.errorKind && <button type="button" onClick={() => {
            const failed = (['live', 'vod', 'series'] as const).filter((kind) => progress[kind] === 'failed');
            setRetryKinds(failed.slice());
            setRunId((value) => value + 1);
          }}>{t('common.retry', 'Tentar novamente')}</button>}
          {finished && <button type="button" onClick={onClose} autoFocus>{t('common.continue', 'Continuar')}</button>}
        </div>
      </div>
    </section>
  );
};
